import { getLocalizedData } from 'Root/Shared/utils/localeURL'
import './ContentAliasLinkPopup.scss'

import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { getLocaleFromUrl } from 'Root/Shared/utils/edit/editURL'
import { useLocation } from '@reach/router'

export interface ContentItem {
    DisplayText: string
    Alias: string
}

export type LinkType = 'internal' | 'external'

export interface ContentAliasLinkPopupType {
    onSelect: (aliasOrUrl: string, isExternal?: boolean, openInNewTab?: boolean) => void
    onClose: () => void
    defaultLinkType?: LinkType
}

const ContentAliasLinkPopup: FunctionComponent<ContentAliasLinkPopupType> = (props) => {
    const [linkType, setLinkType] = useState<LinkType>(props.defaultLinkType || 'internal')
    const [query, setQuery] = useState<string>('')
    const [contentItems, setContentItems] = useState<ContentItem[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedIndex, setSelectedIndex] = useState<number>(0)

    // External link states
    const [externalUrl, setExternalUrl] = useState<string>('')
    const [openInNewTab, setOpenInNewTab] = useState<boolean>(true)

    const inputRef = useRef<HTMLInputElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)
    const location = useLocation();

    let localePrefix;

    if (process.env.PROJECT === "EasyProjectGatsby") {
        if (process.env.LOCALE && ["cs", "en", "hu"].includes(process.env.LOCALE)) {
            localePrefix = process.env.LOCALE
        }
    } else {
        if (process.env.LOCALE && ["en"].includes(process.env.LOCALE)) {
            localePrefix = process.env.LOCALE
        }
    }

    const localizedConfigData = getLocalizedData(
        getLocaleFromUrl(location.pathname, "edit", localePrefix),
        process.env.PROJECT as string
    ) ?? getLocalizedData(process.env.LOCALE!, process.env.PROJECT!)

    useEffect(() => {
        inputRef.current?.focus()

        // Close on outside click
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                props.onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (linkType === 'internal') {
            const delayDebounce = setTimeout(() => {
                if (query.length >= 0) {
                    fetchContentItems()
                }
            }, 300)

            return () => clearTimeout(delayDebounce)
        }
    }, [query, linkType])

    const fetchContentItems = async () => {
        setLoading(true)
        try {
            const response = await fetch(
                `${process.env.GATSBY_ORCHARD_API_URL}/api/wysiwyg/content-items?query=${encodeURIComponent(query)}&culture=${localizedConfigData.isoCode.toLowerCase()}`,
                {
                    credentials: 'include',
                }
            )
            const data = await response.json()
            setContentItems(data)
            setSelectedIndex(0)
        } catch (error) {
            console.error('Error fetching content items:', error)
            setContentItems([])
        } finally {
            setLoading(false)
        }
    }

    const handleSelect = (item: ContentItem) => {
        props.onSelect(item.Alias, false)
    }

    const handleExternalLinkSubmit = () => {
        if (externalUrl) {
            props.onSelect(externalUrl, true, openInNewTab)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setSelectedIndex((prev) => Math.min(prev + 1, contentItems.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setSelectedIndex((prev) => Math.max(prev - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (contentItems[selectedIndex]) {
                handleSelect(contentItems[selectedIndex])
            }
        } else if (e.key === 'Escape') {
            e.preventDefault()
            props.onClose()
        }
    }

    return (
        <div className="ContentAliasLinkPopup" ref={popupRef}>
            <div className="ContentAliasLinkPopup-header">
                <h3>Insert Link</h3>
                <button
                    className="ContentAliasLinkPopup-close"
                    onClick={props.onClose}
                    aria-label="Close"
                >
                    ×
                </button>
            </div>

            {/* Link Type Tabs */}
            <div className="ContentAliasLinkPopup-tabs">
                <button
                    className={`ContentAliasLinkPopup-tab ${linkType === 'internal' ? 'ContentAliasLinkPopup-tab--active' : ''}`}
                    onClick={() => setLinkType('internal')}
                >
                    Internal Link
                </button>
                <button
                    className={`ContentAliasLinkPopup-tab ${linkType === 'external' ? 'ContentAliasLinkPopup-tab--active' : ''}`}
                    onClick={() => setLinkType('external')}
                >
                    External Link
                </button>
            </div>

            <div className="ContentAliasLinkPopup-body">
                {linkType === 'internal' ? (
                    <>
                        <input
                            ref={inputRef}
                            type="text"
                            className="ContentAliasLinkPopup-input"
                            placeholder="Search for content..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {loading && (
                            <div className="ContentAliasLinkPopup-loading">Loading...</div>
                        )}
                        {!loading && contentItems.length > 0 && (
                            <ul className="ContentAliasLinkPopup-list">
                                {contentItems.map((item, index) => (
                                    <li
                                        key={item.Alias}
                                        className={`ContentAliasLinkPopup-item ${index === selectedIndex ? 'ContentAliasLinkPopup-item--selected' : ''
                                            }`}
                                        onClick={() => handleSelect(item)}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    >
                                        <div className="ContentAliasLinkPopup-item-title">
                                            {item.DisplayText}
                                        </div>
                                        <div className="ContentAliasLinkPopup-item-alias">
                                            {item.Alias}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {!loading && query && contentItems.length === 0 && (
                            <div className="ContentAliasLinkPopup-empty">No results found</div>
                        )}
                    </>
                ) : (
                    <div className="ContentAliasLinkPopup-external-form">
                        <div className="ContentAliasLinkPopup-form-group">
                            <label htmlFor="external-url">URL</label>
                            <input
                                id="external-url"
                                ref={inputRef}
                                type="url"
                                className="ContentAliasLinkPopup-input"
                                placeholder="https://example.com"
                                value={externalUrl}
                                onChange={(e) => setExternalUrl(e.target.value)}
                            />
                        </div>
                        <div className="ContentAliasLinkPopup-form-group ContentAliasLinkPopup-checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={openInNewTab}
                                    onChange={(e) => setOpenInNewTab(e.target.checked)}
                                />
                                <span>Open in new tab</span>
                            </label>
                        </div>
                        <button
                            className="ContentAliasLinkPopup-submit-btn"
                            onClick={handleExternalLinkSubmit}
                            disabled={!externalUrl}
                        >
                            Insert Link
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ContentAliasLinkPopup



